import {
  Button,
  ButtonMenu,
  Dialog,
  MenuItem,
  Typography,
} from '@frontend/components/ui';
import { Alert } from '@frontend/components/utils';
import {
  useGetProjectID,
  useProjectPermission,
} from '@frontend/editor/data-access';
import {
  useDeleteBillingScheduledChange,
  useDeleteProjectPermission,
  useGetBillingPayment,
  useGetProject,
  useGetProjectPermission,
  usePostProjectInvite,
} from '@frontend/sorghum/data-access';
import { UICtx } from '@frontend/sorghum/external-providers';
import {
  AccountPermission,
  GetProjectPermission,
  ModalTypesEnum,
  UseSettingType,
} from '@frontend/sorghum/interface';
import {
  BillingInformation,
  Box,
  FacebookErrorModal,
  FacebookPagesModal,
  MobileMenuButton,
} from '@frontend/sorghum/ui';
import {
  BUSINESS_ACCOUNT_TUTORIAL_EN,
  BUSINESS_ACCOUNT_TUTORIAL_ZH,
  copyToClipboard,
  useCustomizedTranslation,
  usePath,
} from '@frontend/sorghum/utils';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import { Avatar, Grid, Link, TextField, Theme } from '@mui/material';
import { blue } from '@mui/material/colors';
import i18next from 'i18next';
import { get } from 'lodash';
import { useCallback, useContext, useState } from 'react';
import { Trans } from 'react-i18next';
import {
  BusinessAccountContainerStyled,
  HeadShotContainerStyled,
  InviteInfoContainerStyled,
  ItemContainerStyled,
  ItemInfoContainerStyled,
  ItemsContainerStyled,
  PageContainerStyled,
  PaperStyled,
  RightContainerStyled,
  TitleContainerStyled,
} from './styles';

export interface SorghumFeaturesSettingProps {
  appID: string;
  permission: string;
  mode: string;
  usePage: (
    facebookAppID: string,
    permission: string,
    mode: string,
  ) => UseSettingType;
}

export function SorghumFeaturesSetting({
  appID,
  permission,
  mode,
  usePage,
}: SorghumFeaturesSettingProps) {
  const {
    isConnect,
    refreshPage,
    openModal,
    disconnectPages,
    TeamMemberInviteDescription,
    Icon,
    connectedPageLink,
    connectedPageName,
    accountName,
    socialType,
    socialTypeName,
    planTitle,
    price,
    period,
    billing,
    hasBusinessAccountDescription,
  } = usePage(appID, permission, mode);
  const { t } = useCustomizedTranslation(socialType);

  const UIState = useContext(UICtx);

  const { navigateToUpgrade } = usePath();
  const { data: projectID } = useGetProjectID();
  const { data: project } = useGetProject(projectID);
  // 下期方案設定（payment_information）：效期內改方案時與現行 period 不同，訂閱卡要明示轉換。
  // 只在已訂閱時抓——trial 專案打這支後端會回 500，白白灌 ES 的 5xx log
  const { data: billingPayment, isLoading: isPaymentLoading } =
    useGetBillingPayment(1, billing?.subscribed ? projectID : undefined);
  // lastFour 有值才代表真的載到付款資料；hook 失敗會回 period=1 預設值
  const paymentDataReady = !!billingPayment?.paymentDetail?.lastFour;
  const nextPeriod = paymentDataReady
    ? billingPayment?.planDetail?.period
    : undefined;
  // 載入完成但拿不到付款資料（unknown）時「保守視為可能有排定變更」——
  // 取消變更後端是冪等 no-op，兩個選項都安全；把 unknown 當「無變更」會讓
  // 排定變更的客戶直接進退訂流程（Codex/Gemini review F1 收斂 finding）
  const hasScheduledChange =
    !!billing?.subscribed &&
    !isPaymentLoading &&
    (!paymentDataReady ||
      (!!nextPeriod && !!billing?.period && nextPeriod !== billing.period));
  const [isOpenCancelChoiceModal, setIsOpenCancelChoiceModal] = useState(false);
  const {
    mutate: cancelScheduledChange,
    isLoading: isCancelingScheduledChange,
  } = useDeleteBillingScheduledChange(projectID);
  const { data: projectPermissionListData } = useGetProjectPermission();
  const { mutate: deleteMember } = useDeleteProjectPermission();
  const { isPermissionViewer } = useProjectPermission();

  const [isOpenInviteModal, setIsOpenInviteModal] = useState(false);
  const [isOpenDeleteMemberModal, setIsOpenDeleteMemberModal] = useState(false);
  useState<boolean>(false);
  const [memberData, setMemberData] = useState({
    id: '',
    name: '',
    permission: 2,
  });
  const [inviteLink, setInviteLink] = useState('');
  const [inviteLinkPermission, setInviteLinkPermission] = useState(
    AccountPermission.ADMIN,
  );

  const { mutate: postInviteLink } = usePostProjectInvite();

  const handleConfirmDeleteMember = () => {
    deleteMember(memberData.id);
    Alert.info(
      t('setting.removeModal.successMessage', { userName: memberData.name }),
    );
    setIsOpenDeleteMemberModal(false);
  };

  const projectPermissionList: GetProjectPermission[] = get(
    projectPermissionListData,
    'data',
    [],
  );

  const handleCancelPlan = useCallback(() => {
    // 有排定的方案變更時先拆語意：客戶多半只想反悔變更，直接進退訂流程會把整個訂閱炸掉
    //（2026-09 楊雅筑案：想取消「以為失敗的購買」，實際取消了訂閱，循環三次）
    // 付款資料載入中先不動作，避免競態下繞過選擇 modal（review F1）
    if (billing?.subscribed && isPaymentLoading) {
      return;
    }
    if (hasScheduledChange) {
      setIsOpenCancelChoiceModal(true);
    } else {
      UIState.openModal(ModalTypesEnum.UNSUBSCRIBE_PAYMENT);
    }
  }, [UIState, hasScheduledChange, billing?.subscribed, isPaymentLoading]);

  const handleCancelScheduledChange = useCallback(() => {
    cancelScheduledChange(undefined, {
      onSuccess: (res) => {
        // 成功才關 modal；失敗保留 modal 讓使用者看到狀態可重試（review F2）
        if (res.code === 20000) {
          Alert.info(t('setting.payment.cancelChoice.changeSuccess'));
          setIsOpenCancelChoiceModal(false);
        } else {
          Alert.error(t('setting.payment.cancelChoice.changeFailed'));
        }
      },
      // F4 配套：hook 不再吞錯，網路層失敗走 onError
      onError: () => {
        Alert.error(t('setting.payment.cancelChoice.changeFailed'));
      },
    });
  }, [cancelScheduledChange, t]);

  const handleCancelSubscriptionInstead = useCallback(() => {
    setIsOpenCancelChoiceModal(false);
    UIState.openModal(ModalTypesEnum.UNSUBSCRIBE_PAYMENT);
  }, [UIState]);

  const handleUpgradePlan = useCallback(() => {
    navigateToUpgrade({
      socialType: project?.socialType,
      projectID,
      source: 'setting',
    });
  }, [navigateToUpgrade, project?.socialType, projectID]);

  const handleInviteClick = useCallback(
    (permission: AccountPermission) => {
      setIsOpenInviteModal(true);
      setInviteLinkPermission(permission);
      postInviteLink(permission, {
        onSuccess: (data) => {
          if (data.code === 20000) {
            setInviteLink(data.data.url);
          }
        },
      });
    },
    [postInviteLink],
  );

  return (
    <>
      <PageContainerStyled>
        <MobileMenuButton sx={{ mb: '8px' }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <PaperStyled>
              <Box sx={{ mb: '28px' }}>
                <Typography variant="h5">{t('setting.linkAccount')}</Typography>
              </Box>
              <Box
                sx={(theme: Theme) => ({
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  // 手機：改為可換行（容得下就同一行，容不下才換行），
                  // 避免未連結時連結按鈕被強制推到下一行、或區塊縮成不滿版
                  [theme.breakpoints.down('s')]: {
                    flexWrap: 'wrap',
                    gap: '8px',
                  },
                })}
              >
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: blue[500], marginRight: '16px' }}>
                    <Icon />
                  </Avatar>
                  {isConnect && (
                    <ItemInfoContainerStyled>
                      <Box className="name">
                        <Typography
                          {...(connectedPageLink && {
                            sx: {
                              cursor: 'pointer',
                            },
                            onClick: () =>
                              window.open(connectedPageLink, '_blank'),
                            variant: 'overline',
                          })}
                        >
                          {connectedPageName}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption">
                          {t('setting.associate')}
                          &nbsp;
                        </Typography>
                        <Typography variant="overline">
                          {accountName}
                        </Typography>
                      </Box>
                    </ItemInfoContainerStyled>
                  )}
                  {!isConnect && (
                    <Box sx={{ ml: '10px' }}>{socialTypeName}</Box>
                  )}
                </Box>
                <Box>
                  {!isConnect && (
                    <Button
                      variant="text"
                      onClick={openModal}
                      disabled={isPermissionViewer}
                    >
                      {t('setting.connect')}
                    </Button>
                  )}
                  {isConnect && (
                    <>
                      <Button
                        variant="text"
                        color="error"
                        onClick={disconnectPages}
                        disabled={isPermissionViewer}
                      >
                        {t('setting.disconnect')}
                      </Button>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={refreshPage && refreshPage}
                        disabled={isPermissionViewer}
                      >
                        {t('setting.refresh')}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
              {hasBusinessAccountDescription && !isConnect && (
                <BusinessAccountContainerStyled>
                  <Typography variant="body2" color="info.dark">
                    <Trans
                      i18nKey={t('setting.businessAccountDescription')}
                      components={[
                        <Link
                          href={
                            i18next.language.includes('zh')
                              ? BUSINESS_ACCOUNT_TUTORIAL_ZH
                              : BUSINESS_ACCOUNT_TUTORIAL_EN
                          }
                          underline="always"
                          target="_blank"
                          sx={{ color: 'info.dark' }}
                        />,
                      ]}
                    />
                  </Typography>
                </BusinessAccountContainerStyled>
              )}
            </PaperStyled>
          </Grid>

          <Grid item xs={12} md={6}>
            <RightContainerStyled>
              <PaperStyled>
                <Typography variant="h5" color="grey.800">
                  {t('setting.payment.billing')}
                </Typography>
                <BillingInformation
                  planTitle={planTitle}
                  billing={billing}
                  price={price}
                  period={period}
                  project={project}
                  handleUpgradePlan={handleUpgradePlan}
                  isPermissionViewer={isPermissionViewer}
                  handleCancelPlan={handleCancelPlan}
                  nextPeriod={nextPeriod}
                />
              </PaperStyled>
              <PaperStyled className="members">
                <TitleContainerStyled>
                  <Typography variant="h5">
                    {t('setting.teamMembers')}
                  </Typography>
                  <ButtonMenu
                    title={t('setting.inviteButton.invite')}
                    endIcon={<ArrowDropDownOutlinedIcon />}
                    disabled={isPermissionViewer}
                  >
                    <MenuItem
                      onClick={() => handleInviteClick(AccountPermission.ADMIN)}
                      content={t('setting.inviteButton.admin')}
                    />
                    <MenuItem
                      onClick={() =>
                        handleInviteClick(AccountPermission.VIEWER)
                      }
                      content={t('setting.inviteButton.viewer')}
                    />
                  </ButtonMenu>
                </TitleContainerStyled>
                <TeamMemberInviteDescription />
                <ItemsContainerStyled>
                  {projectPermissionList.map((item, index) => (
                    <ItemContainerStyled key={item.id}>
                      <Box className="left-side-items">
                        <HeadShotContainerStyled
                          sx={{
                            ...(item.headshot && {
                              backgroundImage: `url(${item.headshot})`,
                            }),
                          }}
                        />
                        <ItemInfoContainerStyled>
                          <Box className="name">
                            <Typography variant="body1" color="grey.900">
                              {item.name} &nbsp;
                            </Typography>
                            {index === 0 && (
                              <Typography variant="caption" color="grey.900">
                                {`(${t('setting.you')})`}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" color="grey.700">
                            {item.permission === AccountPermission.OWNER
                              ? t('setting.role.owner')
                              : item.permission === AccountPermission.ADMIN
                              ? t('setting.role.admin')
                              : t('setting.role.viewer')}
                          </Typography>
                        </ItemInfoContainerStyled>
                      </Box>
                      {index !== 0 && (
                        <Button
                          onClick={() => {
                            setMemberData({
                              id: item.id,
                              name: item.name,
                              permission: item.permission,
                            });
                            setIsOpenDeleteMemberModal(true);
                          }}
                          disabled={isPermissionViewer}
                        >
                          {t('common.remove')}
                        </Button>
                      )}
                    </ItemContainerStyled>
                  ))}
                </ItemsContainerStyled>
              </PaperStyled>
            </RightContainerStyled>
          </Grid>
        </Grid>
        <FacebookPagesModal
          openedPage="setting"
          mode={mode}
          facebookAppID={appID}
        />
        <FacebookErrorModal facebookAppID={appID} mode={mode} />
      </PageContainerStyled>
      <Dialog
        id="invite-modal"
        size="s"
        title={t('setting.inviteModal.title', {
          role:
            inviteLinkPermission === AccountPermission.ADMIN
              ? t('setting.role.adminLowercase')
              : t('setting.role.viewerLowercase'),
        })}
        open={isOpenInviteModal}
        cancelBtnText={t('common.close')}
        handleClose={() => setIsOpenInviteModal(false)}
        closeBtn={false}
      >
        <Typography variant="body1">
          {t('setting.inviteModal.description')}
        </Typography>
        <InviteInfoContainerStyled>
          <TextField className="input-field" size="small" value={inviteLink} />
          <Button
            variant="contained"
            sx={{ textTransform: 'capitalize' }}
            onClick={() => {
              copyToClipboard(
                inviteLink,
                document.getElementById('invite-modal') as HTMLElement,
              );
              Alert.info(t('setting.infoLinkCopied'));
            }}
          >
            {t('common.copy')}
          </Button>
        </InviteInfoContainerStyled>
      </Dialog>
      <Dialog
        size="xs"
        title={t('setting.removeModal.title', {
          role:
            memberData.permission === AccountPermission.ADMIN
              ? t('setting.role.admin')
              : t('setting.role.viewer'),
          userName: memberData.name,
          projectName: project?.projectName as string,
        })}
        open={isOpenDeleteMemberModal}
        cancelBtnText={t('common.cancel')}
        confirmBtnText={t('common.remove')}
        handleClose={() => setIsOpenDeleteMemberModal(false)}
        handleConfirm={handleConfirmDeleteMember}
        closeBtn={false}
        color="error"
        isNegative
      >
        <Typography variant="body1">
          {t('setting.removeModal.description', {
            role:
              memberData.permission === AccountPermission.ADMIN
                ? t('setting.role.admin')
                : t('setting.role.viewer'),
          })}
        </Typography>
      </Dialog>
      {/* 有排定方案變更時的取消拆語意：取消變更（保留訂閱）vs 取消整個訂閱 */}
      <Dialog
        size="xs"
        title={t('setting.payment.cancelChoice.title')}
        open={isOpenCancelChoiceModal}
        // 請求進行中不可關閉/重送（F5/F6 防雙擊）
        handleClose={() =>
          !isCancelingScheduledChange && setIsOpenCancelChoiceModal(false)
        }
      >
        <Typography variant="body1" sx={{ mb: '16px' }}>
          {t('setting.payment.cancelChoice.description')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Button
            id="cancel_scheduled_change"
            variant="contained"
            disabled={isCancelingScheduledChange}
            onClick={handleCancelScheduledChange}
          >
            {t('setting.payment.cancelChoice.cancelChange')}
          </Button>
          <Button
            id="cancel_subscription_instead"
            variant="outlined"
            color="error"
            disabled={isCancelingScheduledChange}
            onClick={handleCancelSubscriptionInstead}
          >
            {t('setting.payment.cancelChoice.cancelSubscription')}
          </Button>
        </Box>
      </Dialog>
    </>
  );
}

export default SorghumFeaturesSetting;
